/**
 * Фильтры журнала изменений: колонка 240 px слева (§8).
 *
 * Сверху — четыре быстрых чипа («Сегодня», «Моё», «Откаты», «Системные»), под
 * ними семь селектов. Любое состояние фильтров живёт в query-строке (§10.5):
 * ссылку на отфильтрованный журнал можно скинуть коллеге.
 *
 * Серверу уходит то, что он умеет: автор, период, бренд, сущность, поле, канал.
 * Статус пакета («откатан», «это откат», «системный») сервер не фильтрует, и
 * честнее отобрать его на загруженной странице и сказать об этом подписью, чем
 * молча показывать неправильное «из 240».
 */
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { ChannelCode, EntityType } from '@shared/types.ts'
import {
  Button, Chip, IconChevronDown, IconSearch, Input, Popover, Select,
} from '../../components/index.js'
import type { SelectOption } from '../../components/index.js'
import { useApp } from '../../store/app-store.js'
import {
  formatDayMonth, historyEntityOptions, historyFieldOptions, historyTexts,
} from './texts.history.js'
import { getLastVisitAt } from './visit-mark.js'
import './HistoryFilters.css'

export type HistoryPeriod = 'all' | 'today' | 'd7' | 'd30' | 'month' | 'visit'
export type HistoryStatus = 'all' | 'committed' | 'reverted' | 'revert' | 'system'

export interface HistoryFilter {
  author: number | null
  period: HistoryPeriod
  brandId: number | null
  /** Поиск по названию бренда внутри журнала: подсвечивает вхождения (§8). */
  brandQuery: string
  fieldCode: string | null
  entityType: EntityType | null
  channel: ChannelCode | null
  status: HistoryStatus
}

export const emptyHistoryFilter: HistoryFilter = {
  author: null,
  period: 'all',
  brandId: null,
  brandQuery: '',
  fieldCode: null,
  entityType: null,
  channel: null,
  status: 'all',
}

const PERIODS: HistoryPeriod[] = ['all', 'today', 'd7', 'd30', 'month', 'visit']
const STATUSES: HistoryStatus[] = ['all', 'committed', 'reverted', 'revert', 'system']

function startOfToday(): Date {
  const date = new Date()
  date.setHours(0, 0, 0, 0)
  return date
}

/** Границы периода в ISO. `visit` берёт метку прошлого визита (§8.1). */
export function periodRange(period: HistoryPeriod, userId: number): { from?: string; to?: string } {
  switch (period) {
    case 'today':
      return { from: startOfToday().toISOString() }
    case 'd7':
      return { from: new Date(Date.now() - 7 * 86_400_000).toISOString() }
    case 'd30':
      return { from: new Date(Date.now() - 30 * 86_400_000).toISOString() }
    case 'month': {
      const now = new Date()
      return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString() }
    }
    case 'visit': {
      const mark = getLastVisitAt(userId)
      return mark ? { from: mark } : {}
    }
    default:
      return {}
  }
}

/** Параметры для `api.changeSets`. Статус здесь не участвует — он клиентский. */
export function historyServerParams(
  filter: HistoryFilter, userId: number, page: number,
): Record<string, string | number | undefined> {
  const range = periodRange(filter.period, userId)
  return {
    page,
    author: filter.author ?? undefined,
    from: range.from,
    to: range.to,
    brandId: filter.brandId ?? undefined,
    entityType: filter.entityType ?? undefined,
    fieldCode: filter.fieldCode ?? undefined,
    channel: filter.channel ?? undefined,
  }
}

/** Фильтры → query-строка (§10.5). Пустые значения не пишем. */
export function historyFilterToParams(filter: HistoryFilter, page: number): URLSearchParams {
  const params = new URLSearchParams()
  if (filter.author != null) params.set('author', String(filter.author))
  if (filter.period !== 'all') params.set('period', filter.period)
  if (filter.brandId != null) params.set('brand', String(filter.brandId))
  if (filter.brandQuery.trim() !== '') params.set('q', filter.brandQuery.trim())
  if (filter.fieldCode) params.set('field', filter.fieldCode)
  if (filter.entityType) params.set('entity', filter.entityType)
  if (filter.channel) params.set('ch', filter.channel)
  if (filter.status !== 'all') params.set('status', filter.status)
  if (page > 1) params.set('page', String(page))
  return params
}

function asNumber(raw: string | null): number | null {
  if (!raw) return null
  const value = Number(raw)
  return Number.isInteger(value) && value > 0 ? value : null
}

/** Query-строка → фильтры. Неизвестные значения игнорируются молча (§10.5). */
export function historyFilterFromParams(params: URLSearchParams): { filter: HistoryFilter; page: number } {
  const rawPeriod = params.get('period')
  const rawStatus = params.get('status')
  const rawEntity = params.get('entity')
  const rawField = params.get('field')
  const rawChannel = params.get('ch')

  const period = PERIODS.find((p) => p === rawPeriod) ?? 'all'
  const status = STATUSES.find((s) => s === rawStatus) ?? 'all'
  const entityType = historyEntityOptions.find((o) => o.value === rawEntity)?.value ?? null
  const fieldCode = historyFieldOptions.find((o) => o.value === rawField)?.value ?? null
  const channel: ChannelCode | null =
    rawChannel === 'retail' || rawChannel === 'b2b' ? rawChannel : null

  const page = asNumber(params.get('page')) ?? 1

  return {
    filter: {
      author: asNumber(params.get('author')),
      period,
      brandId: asNumber(params.get('brand')),
      brandQuery: params.get('q') ?? '',
      fieldCode,
      entityType,
      channel,
      status,
    },
    page,
  }
}

export function historyFilterIsEmpty(filter: HistoryFilter): boolean {
  return filter.author === null
    && filter.period === 'all'
    && filter.brandId === null
    && filter.brandQuery.trim() === ''
    && filter.fieldCode === null
    && filter.entityType === null
    && filter.channel === null
    && filter.status === 'all'
}

/** Отбор по статусу делается на клиенте — сервер этого параметра не знает. */
export function matchesHistoryStatus(
  item: { status: 'committed' | 'reverted'; isSystem: boolean; revertsChangeSetId: number | null },
  status: HistoryStatus,
): boolean {
  switch (status) {
    case 'committed': return item.status === 'committed' && !item.isSystem && item.revertsChangeSetId === null
    case 'reverted': return item.status === 'reverted'
    case 'revert': return item.revertsChangeSetId !== null
    case 'system': return item.isSystem
    default: return true
  }
}

export const historyStatusIsClientSide = (filter: HistoryFilter): boolean => filter.status !== 'all'

// ─────────────────────────────── подсветка ──────────────────────────────────

function Highlight({ text, query }: { text: string; query: string }) {
  const needle = query.trim()
  if (needle.length === 0) return <>{text}</>
  const hit = text.toLowerCase().indexOf(needle.toLowerCase())
  if (hit < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, hit)}
      <mark className="hfilters__hl">{text.slice(hit, hit + needle.length)}</mark>
      {text.slice(hit + needle.length)}
    </>
  )
}

// ─────────────────────────────── компонент ──────────────────────────────────

export interface HistoryFiltersProps {
  value: HistoryFilter
  onChange: (next: HistoryFilter) => void
  currentUserId: number
}

type OpenKey = 'author' | 'period' | 'brand' | 'field' | 'entity' | 'channel' | 'status' | null

export function HistoryFilters({ value, onChange, currentUserId }: HistoryFiltersProps) {
  const users = useApp((s) => s.bootstrap?.users ?? [])
  const channels = useApp((s) => s.bootstrap?.channels ?? [])
  const brands = useApp((s) => s.brands)
  const brandsById = useApp((s) => s.brandsById)

  const [open, setOpen] = useState<OpenKey>(null)
  const [brandAnchor, setBrandAnchor] = useState<HTMLButtonElement | null>(null)
  const [brandSearch, setBrandSearch] = useState(value.brandQuery)

  const patch = (next: Partial<HistoryFilter>) => onChange({ ...value, ...next })

  const authorOptions: SelectOption[] = useMemo(
    () => users
      .filter((user) => user.isActive || user.id === value.author)
      .map((user) => ({ value: String(user.id), label: user.shortName })),
    [users, value.author],
  )

  const periodOptions: SelectOption[] = useMemo(() => {
    const mark = getLastVisitAt(currentUserId)
    return [
      { value: 'today', label: historyTexts.period.today },
      { value: 'd7', label: historyTexts.period.d7 },
      { value: 'd30', label: historyTexts.period.d30 },
      { value: 'month', label: historyTexts.period.month },
      {
        value: 'visit',
        label: mark ? historyTexts.period.visit(formatDayMonth(mark)) : historyTexts.period.visitUnknown,
        disabled: !mark,
      },
    ]
  }, [currentUserId])

  const fieldOptions: SelectOption[] = historyFieldOptions
  const entityOptions: SelectOption[] = historyEntityOptions.map((o) => ({ value: o.value, label: o.label }))
  const channelOptions: SelectOption[] = channels.map((c) => ({ value: c.code, label: c.name }))
  const statusOptions: SelectOption[] = STATUSES
    .filter((code) => code !== 'all')
    .map((code) => ({ value: code, label: historyTexts.status[code] }))

  const foundBrands = useMemo(() => {
    const needle = brandSearch.trim().toLowerCase()
    const list = needle === ''
      ? brands
      : brands.filter((brand) => brand.name.toLowerCase().includes(needle))
    return list.slice(0, 80)
  }, [brands, brandSearch])

  const selectedBrand = value.brandId != null ? brandsById.get(value.brandId) ?? null : null

  return (
    <aside className="hfilters" aria-label={historyTexts.filters.panelLabel}>
      <div className="hfilters__quick" role="group" aria-label={historyTexts.filters.heading}>
        <Chip
          label={historyTexts.quick.today}
          state={value.period === 'today' ? 'on' : 'off'}
          onToggle={() => patch({ period: value.period === 'today' ? 'all' : 'today' })}
        />
        <Chip
          label={historyTexts.quick.mine}
          state={value.author === currentUserId ? 'on' : 'off'}
          onToggle={() => patch({ author: value.author === currentUserId ? null : currentUserId })}
        />
        <Chip
          label={historyTexts.quick.reverts}
          state={value.status === 'revert' ? 'on' : 'off'}
          onToggle={() => patch({ status: value.status === 'revert' ? 'all' : 'revert' })}
        />
        <Chip
          label={historyTexts.quick.system}
          state={value.status === 'system' ? 'on' : 'off'}
          onToggle={() => patch({ status: value.status === 'system' ? 'all' : 'system' })}
        />
      </div>

      <div className="hfilters__fields">
        <Field label={historyTexts.filters.author}>
          <Select
            label={historyTexts.filters.author}
            value={value.author != null ? String(value.author) : null}
            options={authorOptions}
            emptyLabel={historyTexts.filters.anyAuthor}
            open={open === 'author'}
            onOpenChange={(next) => setOpen(next ? 'author' : null)}
            onChange={(next) => patch({ author: next === null ? null : Number(next) })}
          />
        </Field>

        <Field label={historyTexts.filters.period}>
          <Select
            label={historyTexts.filters.period}
            value={value.period === 'all' ? null : value.period}
            options={periodOptions}
            emptyLabel={historyTexts.period.all}
            open={open === 'period'}
            onOpenChange={(next) => setOpen(next ? 'period' : null)}
            onChange={(next) => patch({ period: PERIODS.find((p) => p === next) ?? 'all' })}
          />
        </Field>

        <Field label={historyTexts.filters.brand}>
          <button
            ref={setBrandAnchor}
            type="button"
            className="hfilters__brand"
            aria-haspopup="dialog"
            aria-expanded={open === 'brand'}
            onClick={() => setOpen(open === 'brand' ? null : 'brand')}
          >
            <span className={selectedBrand ? 'hfilters__brand-value' : 'hfilters__brand-value hfilters__brand-value--empty'}>
              {selectedBrand ? selectedBrand.name : historyTexts.filters.anyBrand}
            </span>
            <IconChevronDown />
          </button>
          <Popover
            open={open === 'brand'}
            anchor={brandAnchor}
            onClose={() => {
              // Подсветку вхождений фиксируем при закрытии, а не на каждой
              // букве: иначе query-строка копила бы по записи на символ.
              setOpen(null)
              if (brandSearch !== value.brandQuery) patch({ brandQuery: brandSearch })
            }}
            role="dialog"
            matchAnchorWidth
            minWidth={240}
            className="hfilters__pop"
          >
            <div className="hfilters__search">
              <Input
                autoFocus
                value={brandSearch}
                aria-label={historyTexts.filters.brandSearchLabel}
                placeholder={historyTexts.filters.brandSearchPlaceholder}
                iconBefore={<IconSearch />}
                onChange={(event) => setBrandSearch(event.target.value)}
              />
            </div>
            <ul className="hfilters__brands" aria-label={historyTexts.filters.brandPickLabel}>
              <li>
                <button
                  type="button"
                  className="hfilters__brand-item"
                  onClick={() => {
                    patch({ brandId: null, brandQuery: brandSearch })
                    setOpen(null)
                  }}
                >
                  {historyTexts.filters.anyBrand}
                </button>
              </li>
              {foundBrands.map((brand) => (
                <li key={brand.id}>
                  <button
                    type="button"
                    className="hfilters__brand-item"
                    aria-pressed={brand.id === value.brandId}
                    onClick={() => {
                      patch({ brandId: brand.id, brandQuery: brandSearch })
                      setOpen(null)
                    }}
                  >
                    <Highlight text={brand.name} query={brandSearch} />
                  </button>
                </li>
              ))}
              {foundBrands.length === 0 ? (
                <li className="hfilters__brand-none t-meta">
                  {historyTexts.filters.brandNothing(brandSearch.trim())}
                </li>
              ) : null}
            </ul>
          </Popover>
        </Field>

        <Field label={historyTexts.filters.field}>
          <Select
            label={historyTexts.filters.field}
            value={value.fieldCode}
            options={fieldOptions}
            emptyLabel={historyTexts.filters.anyField}
            open={open === 'field'}
            onOpenChange={(next) => setOpen(next ? 'field' : null)}
            onChange={(next) => patch({ fieldCode: next })}
          />
        </Field>

        <Field label={historyTexts.filters.entity}>
          <Select
            label={historyTexts.filters.entity}
            value={value.entityType}
            options={entityOptions}
            emptyLabel={historyTexts.filters.anyEntity}
            open={open === 'entity'}
            onOpenChange={(next) => setOpen(next ? 'entity' : null)}
            onChange={(next) => patch({
              entityType: historyEntityOptions.find((o) => o.value === next)?.value ?? null,
            })}
          />
        </Field>

        <Field label={historyTexts.filters.channel}>
          <Select
            label={historyTexts.filters.channel}
            value={value.channel}
            options={channelOptions}
            emptyLabel={historyTexts.filters.anyChannel}
            open={open === 'channel'}
            onOpenChange={(next) => setOpen(next ? 'channel' : null)}
            onChange={(next) => patch({ channel: next === 'retail' || next === 'b2b' ? next : null })}
          />
        </Field>

        <Field label={historyTexts.filters.status} hint={historyTexts.filters.clientSide}>
          <Select
            label={historyTexts.filters.status}
            value={value.status === 'all' ? null : value.status}
            options={statusOptions}
            emptyLabel={historyTexts.filters.anyStatus}
            open={open === 'status'}
            onOpenChange={(next) => setOpen(next ? 'status' : null)}
            onChange={(next) => patch({ status: STATUSES.find((s) => s === next) ?? 'all' })}
          />
        </Field>
      </div>

      <div className="hfilters__foot">
        <Button
          variant="ghost"
          size="s"
          disabled={historyFilterIsEmpty(value)}
          onClick={() => {
            setBrandSearch('')
            onChange(emptyHistoryFilter)
          }}
        >
          {historyTexts.filters.reset}
        </Button>
      </div>
    </aside>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="hfilters__field">
      <span className="hfilters__label t-label">{label}</span>
      {children}
      {hint ? <span className="hfilters__hint t-meta">{hint}</span> : null}
    </div>
  )
}
